import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  ReferenceLine,
} from 'recharts';
import {
  useWeatherInPortApi,
  WeatherInPortResponse,
  MonthlyWeatherData,
  MONTH_KEYS,
  MONTH_NAMES,
  INTENSITY_LABELS,
  INTENSITY_THRESHOLDS,
} from '../../api/ApiWeatherInPort/ApiWeatherInPort';
import './WeatherInPort.scss';

const INTENSITY_KEYS = ['pct_dry', 'pct_light', 'pct_moderate_low', 'pct_moderate', 'pct_moderate_high', 'pct_heavy'] as const;

const WeatherInPort: React.FC = () => {
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const id = query.get('id');
  const fromParam = query.get('from');
  const toParam = query.get('to');
  const src = query.get('src');

  const { fetchWeatherInPort } = useWeatherInPortApi();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<WeatherInPortResponse | null>(null);
  const [includeLight, setIncludeLight] = useState(false);

  useEffect(() => {
    if (!id) return;

    const loadData = async () => {
      setLoading(true);
      setError(null);

      const result = await fetchWeatherInPort(id, src);

      if (result.ok && result.data) {
        setData(result.data);
      } else {
        setError(result.message || 'Failed to load weather in port data');
      }

      setLoading(false);
    };

    loadData();
  }, [id, src, fetchWeatherInPort]);

  // Check if a month is in the voyage period based on from/to params
  const isMonthInPeriod = useCallback((monthKey: string): boolean => {
    if (!fromParam || !toParam) return false;

    const fromMonth = parseInt(fromParam.split('-')[0], 10);
    const toMonth = parseInt(toParam.split('-')[0], 10);
    const month = parseInt(monthKey, 10);

    if (fromMonth <= toMonth) {
      return month >= fromMonth && month <= toMonth;
    } else {
      // Handles year wrap-around (e.g., Nov to Feb)
      return month >= fromMonth || month <= toMonth;
    }
  }, [fromParam, toParam]);

  // Prepare chart data with breakdown by intensity
  const chartData = useMemo(() => {
    if (!data) return [];

    return MONTH_KEYS.map(monthKey => {
      const monthData = data.monthly[monthKey] as MonthlyWeatherData;

      return {
        month: MONTH_NAMES[monthKey],
        monthKey,
        isActive: isMonthInPeriod(monthKey),
        light: includeLight ? monthData.pct_light : 0,
        moderateLow: monthData.pct_moderate_low,
        moderate: monthData.pct_moderate,
        moderateHigh: monthData.pct_moderate_high,
        heavy: monthData.pct_heavy,
      };
    });
  }, [data, includeLight, isMonthInPeriod]);

  // Colors for each intensity category
  const INTENSITY_COLORS = {
    light: '#87ceeb',
    moderateLow: '#ffc107',
    moderate: '#fd7e14',
    moderateHigh: '#dc3545',
    heavy: '#8b0000',
  };

  if (!id) {
    return (
      <div className="weather-inport-container">
        <div className="wip-message wip-error">
          <i className="bi bi-exclamation-triangle"></i>
          <p>No port ID provided. Please provide an ID in the URL.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="weather-inport-container">
        <div className="wip-message wip-loading">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p>Loading weather in port data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="weather-inport-container">
        <div className="wip-message wip-error">
          <i className="bi bi-exclamation-triangle"></i>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="weather-inport-container">
      <div className="wip-header">
        <h1>Weather In Port Analysis</h1>
        {data && <p className="wip-subtitle">{data.port_name}</p>}
        {fromParam && toParam && (
          <p className="wip-period">
            <i className="bi bi-calendar3"></i>
            Port stay: {fromParam} to {toParam}
          </p>
        )}
      </div>

      {data && (
        <>
          {/* Summary Panel */}
          {fromParam && toParam && (
            <section className="wip-section wip-summary-section">
              <h2>
                <i className="bi bi-graph-up"></i>
                Port Stay Summary
              </h2>
              <div className="summary-cards">
                {(() => {
                  // Calculate weighted monthly averages based on days in each month
                  const [fromMonth, fromDay] = fromParam.split('-').map(Number);
                  const [toMonth, toDay] = toParam.split('-').map(Number);
                  const daysInMonth = [0, 31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

                  // Build weighted months array: { month, days }
                  const weightedMonths: { month: number; days: number }[] = [];

                  if (fromMonth === toMonth) {
                    // Same month: just count the days
                    weightedMonths.push({ month: fromMonth, days: toDay - fromDay + 1 });
                  } else if (fromMonth < toMonth) {
                    // Spans multiple months within same year
                    // First month: from fromDay to end of month
                    weightedMonths.push({ month: fromMonth, days: daysInMonth[fromMonth] - fromDay + 1 });
                    // Middle months: full months
                    for (let m = fromMonth + 1; m < toMonth; m++) {
                      weightedMonths.push({ month: m, days: daysInMonth[m] });
                    }
                    // Last month: from 1 to toDay
                    weightedMonths.push({ month: toMonth, days: toDay });
                  } else {
                    // Year wrap (e.g., Nov to Feb) - not typical but handle it
                    weightedMonths.push({ month: fromMonth, days: daysInMonth[fromMonth] - fromDay + 1 });
                    for (let m = fromMonth + 1; m <= 12; m++) {
                      weightedMonths.push({ month: m, days: daysInMonth[m] });
                    }
                    for (let m = 1; m < toMonth; m++) {
                      weightedMonths.push({ month: m, days: daysInMonth[m] });
                    }
                    weightedMonths.push({ month: toMonth, days: toDay });
                  }

                  const totalDays = weightedMonths.reduce((sum, wm) => sum + wm.days, 0);

                  // Calculate weighted averages
                  let weightedHeavyRain = 0;
                  let weightedPrecipitation = 0;
                  let weightedDuration = 0;

                  weightedMonths.forEach(({ month, days }) => {
                    const monthData = data.monthly[month.toString()] as MonthlyWeatherData;
                    const weight = days / totalDays;
                    weightedHeavyRain += (monthData.pct_moderate_low + monthData.pct_moderate +
                                          monthData.pct_moderate_high + monthData.pct_heavy) * weight;
                    weightedPrecipitation += monthData.pct_any_precipitation * weight;
                    weightedDuration += monthData.avg_rain_duration * weight;
                  });

                  // Round to nearest 0.5 (same as webhook service)
                  const roundedHeavyRain = Math.round(weightedHeavyRain * 2) / 2;

                  return (
                    <>
                      <div className="summary-card">
                        <div className="card-icon">
                          <i className="bi bi-droplet-half"></i>
                        </div>
                        <div className="card-content">
                          <span className="card-value">{weightedPrecipitation.toFixed(1)}%</span>
                          <span className="card-label">Chance of Rain</span>
                        </div>
                      </div>
                      <div className="summary-card">
                        <div className="card-icon warning">
                          <i className="bi bi-cloud-rain-heavy"></i>
                        </div>
                        <div className="card-content">
                          <span className="card-value">{roundedHeavyRain.toFixed(1)}%</span>
                          <span className="card-label">Rain &gt;1mm/h</span>
                        </div>
                      </div>
                      <div className="summary-card">
                        <div className="card-icon">
                          <i className="bi bi-hourglass-split"></i>
                        </div>
                        <div className="card-content">
                          <span className="card-value">{weightedDuration.toFixed(1)}h</span>
                          <span className="card-label">Avg Rain Duration</span>
                        </div>
                      </div>
                    </>
                  );
                })()}
              </div>
              <p className="summary-footnote">
                <i className="bi bi-info-circle"></i>
                In practice, only rain &gt;1 mm/h is typically considered weather working time (XPD).
                However, if the cargo is sensitive to lighter precipitation, consider increasing XPD accordingly.
              </p>
            </section>
          )}

          {/* Monthly Precipitation Chart */}
          <section className="wip-section wip-chart-section">
            <div className="chart-header">
              <h2>
                <i className="bi bi-bar-chart-fill"></i>
                Monthly Precipitation
              </h2>
              <div className="chart-toggle">
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={includeLight}
                    onChange={(e) => setIncludeLight(e.target.checked)}
                  />
                  <span className="toggle-slider"></span>
                </label>
                <span className="toggle-label">
                  Include light rain (0.1-1.0 mm/h)
                </span>
              </div>
            </div>
            <p className="section-description">
              {includeLight
                ? 'Percentage of hours with any precipitation (>= 0.1 mm/h)'
                : 'Percentage of hours with significant rain (>= 1.0 mm/h)'}
            </p>
            <div className="chart-container">
              <ResponsiveContainer width="100%" height={350}>
                <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                  <XAxis
                    dataKey="month"
                    stroke="rgba(255,255,255,0.6)"
                    tick={{ fill: 'rgba(255,255,255,0.8)', fontSize: 12 }}
                  />
                  <YAxis
                    stroke="rgba(255,255,255,0.6)"
                    tick={{ fill: 'rgba(255,255,255,0.8)', fontSize: 12 }}
                    tickFormatter={(value) => `${value}%`}
                    domain={[0, 100]}
                  />
                  <Tooltip
                    cursor={{ fill: 'rgba(255, 255, 255, 0.05)' }}
                    contentStyle={{
                      backgroundColor: '#1a1a1a',
                      border: '1px solid rgba(41, 230, 255, 0.3)',
                      borderRadius: '8px',
                      color: '#fff',
                    }}
                    formatter={(value, name) => {
                      const labels: Record<string, string> = {
                        light: 'Light (0.1-1.0 mm/h)',
                        moderateLow: 'Moderate Low (1.0-2.0 mm/h)',
                        moderate: 'Moderate (2.0-3.0 mm/h)',
                        moderateHigh: 'Moderate High (3.0-4.0 mm/h)',
                        heavy: 'Heavy (≥4.0 mm/h)',
                      };
                      return [`${Number(value).toFixed(1)}%`, labels[name as string] || name];
                    }}
                    labelFormatter={(label, payload) => {
                      if (!payload || payload.length === 0) return label;
                      let total = 0;
                      payload.forEach(entry => { total += Number(entry.value) || 0; });
                      return `${label} (${total.toFixed(1)}% total)`;
                    }}
                    labelStyle={{ color: '#29e6ff' }}
                  />
                  <Legend
                    wrapperStyle={{ paddingTop: '10px' }}
                    formatter={(value) => {
                      const labels: Record<string, string> = {
                        light: 'Light',
                        moderateLow: 'Mod. Low',
                        moderate: 'Moderate',
                        moderateHigh: 'Mod. High',
                        heavy: 'Heavy',
                      };
                      return <span style={{ color: 'rgba(255,255,255,0.8)', fontSize: '12px' }}>{labels[value] || value}</span>;
                    }}
                  />
                  <ReferenceLine y={10} stroke="rgba(255,255,255,0.4)" strokeDasharray="5 5" />
                  {includeLight && (
                    <Bar dataKey="light" stackId="precipitation" fill={INTENSITY_COLORS.light} />
                  )}
                  <Bar dataKey="moderateLow" stackId="precipitation" fill={INTENSITY_COLORS.moderateLow} />
                  <Bar dataKey="moderate" stackId="precipitation" fill={INTENSITY_COLORS.moderate} />
                  <Bar dataKey="moderateHigh" stackId="precipitation" fill={INTENSITY_COLORS.moderateHigh} />
                  <Bar dataKey="heavy" stackId="precipitation" fill={INTENSITY_COLORS.heavy} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            {fromParam && toParam && (
              <p className="chart-note">
                <span className="active-bar"></span> Highlighted bars indicate port stay period
              </p>
            )}
          </section>

          {/* Monthly Precipitation Intensity Distribution */}
          <section className="wip-section">
            <h2>
              <i className="bi bi-cloud-rain"></i>
              Monthly Precipitation Intensity
            </h2>
            <p className="section-description">
              Percentage of hours in each precipitation intensity category (based on 10-year climatology)
            </p>
            <div className="table-responsive">
              <table className="wip-table wip-intensity-table">
                <thead>
                  <tr>
                    <th className="sticky-col">Intensity</th>
                    <th className="sticky-col-2">Threshold</th>
                    {MONTH_KEYS.map(key => (
                      <th
                        key={key}
                        className={isMonthInPeriod(key) ? 'active-month' : ''}
                      >
                        {MONTH_NAMES[key]}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {INTENSITY_KEYS.map(intensityKey => (
                    <tr key={intensityKey} className={`intensity-row intensity-${intensityKey.replace('pct_', '')}`}>
                      <td className="sticky-col intensity-label">{INTENSITY_LABELS[intensityKey]}</td>
                      <td className="sticky-col-2 threshold-cell">{INTENSITY_THRESHOLDS[intensityKey]}</td>
                      {MONTH_KEYS.map(monthKey => {
                        const monthData = data.monthly[monthKey] as MonthlyWeatherData;
                        const value = monthData[intensityKey as keyof MonthlyWeatherData] as number;
                        return (
                          <td
                            key={monthKey}
                            className={isMonthInPeriod(monthKey) ? 'active-month' : ''}
                          >
                            {value.toFixed(1)}%
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                  {/* Total significant rain row (>1 mm/h) */}
                  <tr className="total-row">
                    <td className="sticky-col intensity-label">Rain (&gt;1 mm/h)</td>
                    <td className="sticky-col-2 threshold-cell">&gt;= 1.0 mm/h</td>
                    {MONTH_KEYS.map(monthKey => {
                      const monthData = data.monthly[monthKey] as MonthlyWeatherData;
                      const significantRain = monthData.pct_moderate_low + monthData.pct_moderate +
                                              monthData.pct_moderate_high + monthData.pct_heavy;
                      return (
                        <td
                          key={monthKey}
                          className={isMonthInPeriod(monthKey) ? 'active-month' : ''}
                        >
                          {significantRain.toFixed(1)}%
                        </td>
                      );
                    })}
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          {/* Monthly Rain Event Statistics */}
          <section className="wip-section">
            <h2>
              <i className="bi bi-clock-history"></i>
              Rain Event Duration Statistics
            </h2>
            <p className="section-description">
              Average duration and frequency of rain events by month
            </p>
            <div className="table-responsive">
              <table className="wip-table wip-duration-table">
                <thead>
                  <tr>
                    <th className="sticky-col">Statistic</th>
                    {MONTH_KEYS.map(key => (
                      <th
                        key={key}
                        className={isMonthInPeriod(key) ? 'active-month' : ''}
                      >
                        {MONTH_NAMES[key]}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="sticky-col stat-label">Avg Duration (hours)</td>
                    {MONTH_KEYS.map(monthKey => {
                      const monthData = data.monthly[monthKey] as MonthlyWeatherData;
                      return (
                        <td
                          key={monthKey}
                          className={isMonthInPeriod(monthKey) ? 'active-month' : ''}
                        >
                          {monthData.avg_rain_duration.toFixed(1)}
                        </td>
                      );
                    })}
                  </tr>
                  <tr>
                    <td className="sticky-col stat-label">Events/Day</td>
                    {MONTH_KEYS.map(monthKey => {
                      const monthData = data.monthly[monthKey] as MonthlyWeatherData;
                      return (
                        <td
                          key={monthKey}
                          className={isMonthInPeriod(monthKey) ? 'active-month' : ''}
                        >
                          {monthData.avg_daily_events.toFixed(2)}
                        </td>
                      );
                    })}
                  </tr>
                  <tr className="event-type-row">
                    <td className="sticky-col stat-label">Short Events (1-2h)</td>
                    {MONTH_KEYS.map(monthKey => {
                      const monthData = data.monthly[monthKey] as MonthlyWeatherData;
                      return (
                        <td
                          key={monthKey}
                          className={isMonthInPeriod(monthKey) ? 'active-month' : ''}
                        >
                          {monthData.pct_short_events.toFixed(1)}%
                        </td>
                      );
                    })}
                  </tr>
                  <tr className="event-type-row">
                    <td className="sticky-col stat-label">Medium Events (3-6h)</td>
                    {MONTH_KEYS.map(monthKey => {
                      const monthData = data.monthly[monthKey] as MonthlyWeatherData;
                      return (
                        <td
                          key={monthKey}
                          className={isMonthInPeriod(monthKey) ? 'active-month' : ''}
                        >
                          {monthData.pct_medium_events.toFixed(1)}%
                        </td>
                      );
                    })}
                  </tr>
                  <tr className="event-type-row">
                    <td className="sticky-col stat-label">Long Events (&gt;6h)</td>
                    {MONTH_KEYS.map(monthKey => {
                      const monthData = data.monthly[monthKey] as MonthlyWeatherData;
                      return (
                        <td
                          key={monthKey}
                          className={isMonthInPeriod(monthKey) ? 'active-month' : ''}
                        >
                          {monthData.pct_long_events.toFixed(1)}%
                        </td>
                      );
                    })}
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          {/* Legend */}
          <section className="wip-section wip-legend">
            <h2>
              <i className="bi bi-info-circle"></i>
              Understanding the Data
            </h2>
            <div className="legend-content">
              <div className="legend-group">
                <h3>Precipitation Intensity Categories</h3>
                <ul>
                  <li><span className="legend-color dry"></span><strong>Dry:</strong> No significant precipitation (&lt; 0.1 mm/h)</li>
                  <li><span className="legend-color light"></span><strong>Light:</strong> Drizzle or light rain (0.1 - 1.0 mm/h)</li>
                  <li><span className="legend-color moderate-low"></span><strong>Moderate Low:</strong> Light to moderate rain (1.0 - 2.0 mm/h)</li>
                  <li><span className="legend-color moderate"></span><strong>Moderate:</strong> Moderate rain (2.0 - 3.0 mm/h)</li>
                  <li><span className="legend-color moderate-high"></span><strong>Moderate High:</strong> Moderate to heavy rain (3.0 - 4.0 mm/h)</li>
                  <li><span className="legend-color heavy"></span><strong>Heavy:</strong> Heavy rain or showers (&gt;= 4.0 mm/h)</li>
                </ul>
              </div>
              <div className="legend-group">
                <h3>Rain Event Duration</h3>
                <ul>
                  <li><strong>Short Events:</strong> Rain lasting 1-2 hours</li>
                  <li><strong>Medium Events:</strong> Rain lasting 3-6 hours</li>
                  <li><strong>Long Events:</strong> Rain lasting more than 6 hours</li>
                </ul>
              </div>
              <div className="legend-group">
                <h3>Port Stay Summary Calculations</h3>
                <ul>
                  <li><strong>Chance of Rain:</strong> Weighted average % of hours with any precipitation (&gt;= 0.1 mm/h), weighted by days in each month of the stay</li>
                  <li><strong>Rain &gt;1mm/h:</strong> Weighted average % of hours with significant rain (Moderate Low + Moderate + Moderate High + Heavy), rounded to nearest 0.5%</li>
                  <li><strong>Avg Rain Duration:</strong> Weighted average duration of rain events based on days in each month</li>
                </ul>
              </div>
              <div className="legend-note">
                <i className="bi bi-database"></i>
                <span>Data based on 10-year ERA5 climatological averages for this port location.</span>
              </div>
            </div>
          </section>

          {fromParam && toParam && (
            <p className="table-note">
              <span className="active-indicator"></span> Highlighted months indicate port stay period
            </p>
          )}
        </>
      )}
    </div>
  );
};

export default WeatherInPort;
